/**
 * @fileoverview General utilities for arrays. Use lodash whenever you
 * can!
 */

import uniq from 'lodash/uniq'
import isEqual from 'lodash/isEqual'

/**
 * Ensure a value is an array without shallow cloning it
 * @param {*} maybeArray
 * @returns {Array}
 */
export function ensureArray(maybeArray) {
  if (Array.isArray(maybeArray)) {
    return maybeArray
  } else if (maybeArray == null) {
    return []
  }

  return [].concat.apply([], [maybeArray])
}

/**
 * Filters an array eliminating non-truthy elements
 * @param {Array} array
 * @returns {Array}
 */
export function hasValue(array) {
  return array.filter(val => !!val)
}

export function toggleValue(array, value) {
  if (array.find(v => isEqual(v, value))) {
    return array.filter(v => !isEqual(v, value))
  } else {
    return array.concat([value])
  }
}

/**
 * Non-destructively sorts an array based on another array
 * @param {Array} array
 * @param {Array} order
 * @param {string} field
 * @param {number} [direction]
 * @returns {Array}
 */
export function sortByOrder(array, order, field, direction = 1) {
  // Copy to prevent destructive sort
  let copy = ensureArray(array).concat()

  return copy.sort(function(a, b) {
    let left = order.indexOf(a[field])
    let right = order.indexOf(b[field])

    if (left === -1 || right === -1) {
      return 0
    }

    return (left > right ? 1 : left < right ? -1 : 0) * direction
  })
}

/**
 * Predicate that describes whether the values in `items`
 * exist in the same order within `sourceOrder`
 * @param {Array} sourceOrder
 * @param {Array} items
 * @returns {Bool}
 */
export function hasOrder(sourceOrder = [], items = []) {
  if (sourceOrder.length !== uniq(sourceOrder).length) {
    throw new Error(
      'The elements in `sourceOrder` must not contain duplicates.'
    )
  }

  // if a source order is not given, then the items are already sorted as best they can be
  if (!sourceOrder.length) {
    return true
  }

  const result = items.reduce(
    (memo, item) => {
      if (!memo.inOrder) {
        return memo
      }

      const index = sourceOrder.indexOf(item)

      if (index < 0) {
        return memo
      }

      if (memo.lastIndex > index) {
        return { ...memo, inOrder: false }
      }

      return { ...memo, lastIndex: index }
    },
    {
      lastIndex: -1,
      inOrder: true
    }
  )

  return result.inOrder
}

/**
 * Toggles presence of the item in the array. If array is falsey,
 * a new array is returned containing the item; if the result is
 * an empty array, the returnIfEmpty value is returned.
 *
 * @param {Array} array
 * @param {*} item
 * @returns {Array}
 */
export function toggleArrayItem(array, item, returnIfEmpty = null) {
  if (!array) {
    array = []
  } else {
    array = [].concat(array)
  }

  if (array.includes(item)) {
    array.splice(array.indexOf(item), 1)
  } else {
    array.push(item)
  }

  if (!array.length) {
    array = returnIfEmpty
  }

  return array
}

/**
 * Checks the property in the object and if found to
 * not be an array, replaces its value with an array
 * with the original value as its single item.
 *
 * @param {Object} obj
 * @param {String} name
 */
export function propAsArray(obj, prop) {
  if (obj[prop] && !Array.isArray(obj[prop])) {
    obj[prop] = [obj[prop]]
  }
}



// WEBPACK FOOTER //
// /home/runner/work/web-interactive/web-interactive/lib/array.js